"use client";

import { ArrowDown, Bot, Check, Copy, Loader2, Send, Trash2, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/shared/drawer";
import { Input } from "@/components/ui/input";
import ReactMarkdown from "react-markdown";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { useChat } from "@ai-sdk/react";
import { useLocalforage } from "@/hooks/use-localforage";
import { usePageContext } from "@/components/chat/page-context";

interface ChatDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function ChatDrawer({ open, onOpenChange }: ChatDrawerProps) {
    const { context } = usePageContext();
    // useLocalforage returns values array matching keys array
    const { values, setItem, isLoaded } = useLocalforage<[any[]]>(["chat-history"], { storeName: "chat" });
    const storedMessages = values[0]; // Extract the first value (chat-history)

    // @ts-ignore
    const { messages, sendMessage, status, setMessages } = useChat({
        onFinish: async () => {
            // We'll rely on the useEffect below to sync messages to storage
        }
    });

    const [input, setInput] = useState("");
    const scrollRef = useRef<HTMLDivElement>(null);
    const viewportRef = useRef<HTMLDivElement>(null);
    const isLoading = status === "submitted" || status === "streaming";
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
    const hasInitializedRef = useRef(false);

    // Initialize messages from storage
    useEffect(() => {
        if (isLoaded && storedMessages && storedMessages.length > 0 && !hasInitializedRef.current) {
            // Filter out any invalid messages that might have been saved
            const validMessages = storedMessages.filter((m: any) => m && (typeof m.content === 'string' || Array.isArray(m.parts)));
            if (validMessages.length > 0) {
                setMessages(validMessages);
            }
            hasInitializedRef.current = true;
        }
    }, [isLoaded, storedMessages, setMessages]);

    // Save messages to storage whenever they change
    useEffect(() => {
        if (messages.length > 0) {
            setItem(messages);
        }
    }, [messages, setItem]);

    // Auto-scroll to bottom on new messages if already at bottom or close to it
    useEffect(() => {
        if (viewportRef.current) {
            const viewport = viewportRef.current;
            const isNearBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 100;

            if (isNearBottom) {
                scrollToBottom();
            }
        }
    }, [messages, status]);

    const scrollToBottom = () => {
        if (viewportRef.current) {
            viewportRef.current.scrollTo({
                top: viewportRef.current.scrollHeight,
                behavior: "smooth"
            });
        }
    };

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const viewport = event.currentTarget;
        const isNearBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 100;
        setShowScrollButton(!isNearBottom);
    };

    const copyToClipboard = (text: string, messageId: string) => {
        navigator.clipboard.writeText(text);
        setCopiedMessageId(messageId);
        toast.success("Copied to clipboard");
        setTimeout(() => setCopiedMessageId(null), 2000);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (input.trim()) {
            sendMessage({ role: "user", content: input } as any, { body: { pageContext: context } });
            setInput("");
        }
    };

    const handleClearChat = () => {
        setMessages([]);
        setItem([]);
    };

    return (
        <Drawer
            title="Wiley Assistant"
            open={open}
            onOpenChange={onOpenChange}
            className="flex flex-col h-full"
        >
            <div className="flex flex-col h-[calc(100vh-70px)] relative">
                <div className="flex justify-between items-center px-4 py-2 border-b bg-muted/30">
                    <span className="text-xs text-muted-foreground truncate max-w-50" title={context?.page || 'Global'}>
                        Context: {context?.page || 'Global'}
                    </span>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleClearChat}
                        title="Clear Chat History"
                        className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
                <ScrollArea
                    className="flex-1 p-4 bg-amber-50 h-0"
                    ref={scrollRef}
                    onScrollCapture={handleScroll} // Capture scroll events
                >
                    {/* We need to attach a ref to the viewport to control scrolling programmatically */}
                    <div ref={viewportRef} className="h-full w-full">
                        <div className="space-y-4 pb-4">
                            {messages.length === 0 && (
                                <div className="text-center text-muted-foreground py-8">
                                    <Bot className="h-12 w-12 mx-auto mb-4 opacity-20" />
                                    <p>Hello! How can I help you today?</p>
                                </div>
                            )}
                            {messages.filter(m => !!m).map((m: any) => {
                                const content = m.content || m.parts?.map((p: any) => p.text).join("") || "";
                                return (
                                    <div
                                        key={m.id}
                                        className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"
                                            }`}
                                    >
                                        {m.role !== "user" && (
                                            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                                <Bot className="h-5 w-5 text-primary" />
                                            </div>
                                        )}
                                        <div className="flex flex-col gap-1 max-w-[85%]">
                                            <div
                                                className={`rounded-lg px-4 py-2 ${m.role === "user"
                                                    ? "bg-primary text-primary-foreground"
                                                    : "bg-muted"
                                                    }`}
                                            >
                                                <div className="prose prose-sm dark:prose-invert">
                                                    <ReactMarkdown>
                                                        {content}
                                                    </ReactMarkdown>
                                                </div>
                                            </div>
                                            {m.role !== "user" && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-6 w-6 self-start text-muted-foreground hover:text-foreground"
                                                    onClick={() => copyToClipboard(content, m.id)}
                                                    title="Copy to clipboard"
                                                >
                                                    {copiedMessageId === m.id ? (
                                                        <Check className="h-3 w-3" />
                                                    ) : (
                                                        <Copy className="h-3 w-3" />
                                                    )}
                                                </Button>
                                            )}
                                        </div>
                                        {m.role === "user" && (
                                            <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                                                <User className="h-5 w-5 text-secondary-foreground" />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {isLoading && (
                                <div className="flex justify-start">
                                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                        <Bot className="h-5 w-5 text-primary" />
                                    </div>
                                    <div className="bg-muted rounded-lg px-4 py-2 ml-3">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </ScrollArea>

                {showScrollButton && (
                    <Button
                        size="icon"
                        variant="secondary"
                        className="absolute bottom-20 left-1/2 -translate-x-1/2 rounded-full shadow-md z-10"
                        onClick={scrollToBottom}
                    >
                        <ArrowDown className="h-4 w-4" />
                    </Button>
                )}

                <div className="p-4 border-t border-border mt-auto bg-background">
                    <form
                        onSubmit={handleSubmit}
                        className="flex gap-2"
                    >
                        <Input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Type your message..."
                            className="flex-1"
                            disabled={isLoading}
                        />
                        <Button type="submit" size="icon" disabled={isLoading || !input.trim()}>
                            <Send className="h-4 w-4" />
                        </Button>
                    </form>
                </div>
            </div>
        </Drawer>
    );
}
